import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Pencil, Plus } from 'lucide-react'
import {
  useGetInvestigationById,
  useUpdateInvestigationNotes,
} from '@/application/services/investigation.service'
import { useAuthStore } from '@/application/stores/auth.store'
import { Button } from '@/ui/components/common/Button'
import { NotesPanel } from '@/ui/components/common/NotesPanel'

/**
 * Notes section for the management investigation leads page. Always visible
 * when there is content or the user can edit. Shows "Analyses et synthèse"
 * H2 title with the edit button aligned to its right. Streamers, admins and
 * the investigation author (moderator) can edit.
 */
export function InvestigationNotesSection({ id }: { id: string }) {
  const { t } = useTranslation()
  const { user } = useAuthStore()
  const { data: investigation } = useGetInvestigationById(id)
  const { mutateAsync: updateNotes } = useUpdateInvestigationNotes()
  const [isEditing, setIsEditing] = useState(false)

  if (!investigation) return null

  const canEditNotes =
    user?.role === 'streamer' ||
    user?.role === 'admin' ||
    (user?.role === 'moderator' && investigation.authorId === user?.id)

  if (!investigation.notes && !canEditNotes) return null

  const hasNotes = !!investigation.notes?.trim()

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-serif text-2xl font-bold">{t('investigation_detail.notes_title')}</h2>
        {canEditNotes && !isEditing && (
          <Button
            size="sm"
            onClick={() => setIsEditing(true)}
            leftIcon={hasNotes ? <Pencil className="w-3.5 h-3.5" /> : <Plus className="w-4 h-4" />}
          >
            {hasNotes ? t('common.notes_edit') : t('common.notes_add')}
          </Button>
        )}
      </div>
      <NotesPanel
        notes={investigation.notes}
        canEdit={canEditNotes}
        isEditing={isEditing}
        onEditingChange={setIsEditing}
        onSave={async (notes) => {
          await updateNotes({ id, notes })
        }}
      />
    </div>
  )
}
